/**
 * 監査ログサービス
 * セキュリティ関連イベントの記録と追跡
 */

import logger from '../utils/logger';

// 監査イベントタイプ
export enum AuditEventType {
  // 認証関連
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGOUT = 'LOGOUT',
  PASSWORD_RESET = 'PASSWORD_RESET',
  MFA_ENABLED = 'MFA_ENABLED',
  MFA_DISABLED = 'MFA_DISABLED',
  
  // 組織管理
  ORGANIZATION_CREATED = 'ORGANIZATION_CREATED',
  ORGANIZATION_UPDATED = 'ORGANIZATION_UPDATED',
  ORGANIZATION_DELETED = 'ORGANIZATION_DELETED',
  INVITATION_SENT = 'INVITATION_SENT',
  INVITATION_ACCEPTED = 'INVITATION_ACCEPTED',
  INVITATION_DECLINED = 'INVITATION_DECLINED',
  INVITATION_EXPIRED = 'INVITATION_EXPIRED',
  MEMBER_ADDED = 'MEMBER_ADDED',
  MEMBER_REMOVED = 'MEMBER_REMOVED',
  ROLE_CHANGED = 'ROLE_CHANGED',
  
  // データアクセス
  DATA_EXPORT = 'DATA_EXPORT',
  DATA_IMPORT = 'DATA_IMPORT',
  SENSITIVE_DATA_ACCESS = 'SENSITIVE_DATA_ACCESS',
  
  // セキュリティ
  SUSPICIOUS_ACTIVITY = 'SUSPICIOUS_ACTIVITY',
  TOKEN_REVOKED = 'TOKEN_REVOKED',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED'
}

// 監査ログエントリ
export interface AuditLogEntry {
  id: string;
  timestamp: string;
  eventType: AuditEventType;
  userId?: string;
  userEmail?: string;
  organizationId?: string;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, any>;
  success: boolean;
  errorMessage?: string;
}

// 監査ログストレージキー
const AUDIT_LOG_KEY = 'salonhub_audit_logs';
const MAX_LOG_ENTRIES = 1000; // 最大保持ログ数

export class AuditLogService {
  /**
   * 監査ログの記録
   */
  static async log(
    eventType: AuditEventType,
    details: {
      userId?: string;
      userEmail?: string;
      organizationId?: string;
      success: boolean;
      errorMessage?: string;
      metadata?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      const entry: AuditLogEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        eventType,
        userId: details.userId,
        userEmail: details.userEmail,
        organizationId: details.organizationId,
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        details: details.metadata,
        success: details.success,
        errorMessage: details.errorMessage
      };

      // LocalStorageに保存（本番環境ではサーバーに送信）
      if (import.meta.env.PROD) {
        // TODO: サーバーAPIに送信
        await this.sendToServer(entry);
      } else {
        // 開発環境ではLocalStorageに保存
        this.saveToLocalStorage(entry);
      }

      // コンソールログ出力（開発環境のみ）
      if (import.meta.env.DEV) {
        const emoji = details.success ? '✅' : '❌';
        console.log(`${emoji} [AUDIT] ${eventType}:`, entry);
      }
    } catch (error) {
      logger.error('監査ログの記録に失敗:', error);
    }
  }

  /**
   * ログイン成功の記録
   */
  static async logLoginSuccess(userId: string, email: string, method: 'email' | 'google' | 'mfa'): Promise<void> {
    await this.log(AuditEventType.LOGIN_SUCCESS, {
      userId,
      userEmail: email,
      success: true,
      metadata: { method }
    });
  }

  /**
   * ログイン失敗の記録
   */
  static async logLoginFailed(email: string, reason: string): Promise<void> {
    await this.log(AuditEventType.LOGIN_FAILED, {
      userEmail: email,
      success: false,
      errorMessage: reason
    });
  }

  /**
   * 組織招待承認の記録
   */
  static async logInvitationAccepted(
    userId: string,
    email: string,
    organizationId: string,
    invitationToken: string
  ): Promise<void> {
    await this.log(AuditEventType.INVITATION_ACCEPTED, {
      userId,
      userEmail: email,
      organizationId,
      success: true,
      metadata: { invitationToken }
    });
  }

  /**
   * 権限変更の記録
   */
  static async logRoleChanged(
    userId: string,
    targetUserId: string,
    organizationId: string,
    oldRole: string,
    newRole: string
  ): Promise<void> {
    await this.log(AuditEventType.ROLE_CHANGED, {
      userId,
      organizationId,
      success: true,
      metadata: {
        targetUserId,
        oldRole,
        newRole
      }
    });
  }

  /**
   * 不審なアクティビティの記録
   */
  static async logSuspiciousActivity(
    description: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    await this.log(AuditEventType.SUSPICIOUS_ACTIVITY, {
      success: false,
      errorMessage: description,
      metadata
    });
  }

  /**
   * 監査ログの取得
   */
  static getAuditLogs(
    filters?: {
      eventType?: AuditEventType;
      userId?: string;
      organizationId?: string;
      startDate?: Date;
      endDate?: Date;
    }
  ): AuditLogEntry[] {
    try {
      const logsJson = localStorage.getItem(AUDIT_LOG_KEY);
      if (!logsJson) return [];

      let logs: AuditLogEntry[] = JSON.parse(logsJson);

      // フィルタリング
      if (filters) {
        if (filters.eventType) {
          logs = logs.filter(log => log.eventType === filters.eventType);
        }
        if (filters.userId) {
          logs = logs.filter(log => log.userId === filters.userId);
        }
        if (filters.organizationId) {
          logs = logs.filter(log => log.organizationId === filters.organizationId);
        }
        if (filters.startDate) {
          logs = logs.filter(log => new Date(log.timestamp) >= filters.startDate!);
        }
        if (filters.endDate) {
          logs = logs.filter(log => new Date(log.timestamp) <= filters.endDate!);
        }
      }

      // 最新順にソート
      return logs.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    } catch (error) {
      logger.error('監査ログの取得に失敗:', error);
      return [];
    }
  }

  /**
   * LocalStorageに保存
   */
  private static saveToLocalStorage(entry: AuditLogEntry): void {
    try {
      const logsJson = localStorage.getItem(AUDIT_LOG_KEY);
      let logs: AuditLogEntry[] = logsJson ? JSON.parse(logsJson) : [];

      // 新しいエントリを追加
      logs.unshift(entry);

      // 最大数を超えたら古いエントリを削除
      if (logs.length > MAX_LOG_ENTRIES) {
        logs = logs.slice(0, MAX_LOG_ENTRIES);
      }

      localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(logs));
    } catch (error) {
      logger.error('LocalStorageへの監査ログ保存に失敗:', error);
    }
  }

  /**
   * サーバーに送信（本番環境用）
   */
  private static async sendToServer(entry: AuditLogEntry): Promise<void> {
    try {
      // IndexedDBへの永続化
      const queue = await this.getServerQueue();
      queue.push(entry);
      await this.saveToIndexedDB(queue);
      
      // LocalStorageにもフォールバックとして保存
      this.saveToLocalStorage(entry);
      
      // バッチ送信のシミュレーション（10件毎）
      if (queue.length >= 10) {
        await this.flushToServer(queue);
      }
      
      logger.info('[AuditLog] エントリをサーバーキューに追加:', {
        eventType: entry.eventType,
        queueSize: queue.length
      });
    } catch (error) {
      logger.error('[AuditLog] サーバー送信エラー、LocalStorageに保存:', error);
      // エラー時はLocalStorageに保存
      this.saveToLocalStorage(entry);
    }
  }

  /**
   * サーバー送信キューの取得
   */
  private static async getServerQueue(): Promise<AuditLogEntry[]> {
    return new Promise((resolve) => {
      if (!('indexedDB' in window)) {
        resolve([]);
        return;
      }

      const request = indexedDB.open('SalonHubAuditDB', 1);

      request.onerror = () => {
        logger.error('[AuditLog] IndexedDB接続エラー:', request.error);
        resolve([]);
      };

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['auditQueue'], 'readonly');
        const store = transaction.objectStore('auditQueue');
        const getAllRequest = store.getAll();

        getAllRequest.onsuccess = () => {
          db.close();
          resolve(getAllRequest.result || []);
        };

        getAllRequest.onerror = () => {
          db.close();
          logger.error('[AuditLog] キュー取得エラー:', getAllRequest.error);
          resolve([]);
        };
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('auditQueue')) {
          const store = db.createObjectStore('auditQueue', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains('auditLogs')) {
          const store = db.createObjectStore('auditLogs', { keyPath: 'id' });
          store.createIndex('eventType', 'eventType', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * IndexedDBへの保存
   */
  private static async saveToIndexedDB(entries: AuditLogEntry[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open('SalonHubAuditDB', 1);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['auditQueue'], 'readwrite');
        const store = transaction.objectStore('auditQueue');

        // 既存のキューをクリアして新しいエントリを保存
        store.clear();
        
        entries.forEach(entry => {
          store.add(entry);
        });

        transaction.oncomplete = () => {
          db.close();
          logger.info(`[AuditLog] ${entries.length}件のエントリをIndexedDBに保存`);
          resolve();
        };

        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('auditQueue')) {
          const store = db.createObjectStore('auditQueue', { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains('auditLogs')) {
          const store = db.createObjectStore('auditLogs', { keyPath: 'id' });
          store.createIndex('eventType', 'eventType', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * サーバーへのバッチ送信
   */
  private static async flushToServer(entries: AuditLogEntry[]): Promise<void> {
    try {
      // 本番環境ではここで実際のAPI呼び出しを行う
      if (import.meta.env.PROD) {
        // await ApiClient.post('/api/audit-logs/batch', { entries });
        logger.info('[AuditLog] バッチ送信をシミュレート:', {
          count: entries.length,
          firstEntry: entries[0]?.eventType,
          lastEntry: entries[entries.length - 1]?.eventType
        });
      }

      // 送信成功後、永続ストレージに移動
      await this.moveToArchive(entries);
      
      // キューをクリア
      await this.clearQueue();
      
      logger.info(`[AuditLog] ${entries.length}件のエントリをサーバーに送信完了`);
    } catch (error) {
      logger.error('[AuditLog] バッチ送信エラー:', error);
      throw error;
    }
  }

  /**
   * アーカイブへの移動
   */
  private static async moveToArchive(entries: AuditLogEntry[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        resolve();
        return;
      }

      const request = indexedDB.open('SalonHubAuditDB', 1);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['auditLogs'], 'readwrite');
        const store = transaction.objectStore('auditLogs');

        entries.forEach(entry => {
          store.add(entry);
        });

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };

        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      };
    });
  }

  /**
   * キューのクリア
   */
  private static async clearQueue(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        resolve();
        return;
      }

      const request = indexedDB.open('SalonHubAuditDB', 1);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['auditQueue'], 'readwrite');
        const store = transaction.objectStore('auditQueue');

        store.clear();

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };

        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      };
    });
  }

  /**
   * クライアントIPアドレスの取得（可能な範囲で）
   */
  private static getClientIP(): string | undefined {
    // ブラウザからは直接IPアドレスを取得できないため
    // サーバーサイドで取得する必要がある
    return undefined;
  }

  /**
   * 監査ログのエクスポート
   */
  static exportAuditLogs(): string {
    const logs = this.getAuditLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * 監査ログのクリア（開発用）
   */
  static clearAuditLogs(): void {
    if (import.meta.env.DEV) {
      localStorage.removeItem(AUDIT_LOG_KEY);
      logger.info('監査ログをクリアしました');
    }
  }
}

// グローバルに公開（開発用）
if (import.meta.env.DEV) {
  (window as any).AuditLogService = AuditLogService;
}